import { lazy, Suspense, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { AdaptiveDpr } from '@react-three/drei';
import {
  EffectComposer,
  Bloom,
  ToneMapping,
  Vignette,
  ChromaticAberration,
  DepthOfField,
} from '@react-three/postprocessing';
import { ToneMappingMode } from 'postprocessing';
import { BlendFunction } from 'postprocessing';
import * as THREE from 'three';
import { useGameStore } from '../core/store';
import { TunnelScene } from '../systems/tunnel/TunnelScene';
import { useTunnelExitStore } from '../systems/tunnel/tunnelExitStore';
import { FullscreenCanvasSync } from '../systems/ui/FullscreenCanvasSync';

/** Rapier + run lane — only fetched when phase === 'run'. */
const RunWorld = lazy(() =>
  import('./RunWorld').then((m) => ({ default: m.RunWorld })),
);

function PostFX({ tunnel }: { tunnel: boolean }) {
  const quality = useGameStore((s) => s.quality);
  const exitBeat = useTunnelExitStore((s) => s.beat);
  const caOffset = useRef(new THREE.Vector2(0.00025, 0.00025));
  const exitCinematic = exitBeat === 'exit' || exitBeat === 'fade';

  if (tunnel) {
    // Exit walk: NO bloom/composer — postprocessing + stadium lights was
    // blowing the WebGL context before the hero could paint.
    if (exitCinematic) {
      return null;
    }
    return (
      <EffectComposer multisampling={0}>
        <Vignette eskil={false} offset={0.16} darkness={0.42} />
        <ToneMapping mode={ToneMappingMode.ACES_FILMIC} />
      </EffectComposer>
    );
  }

  return (
    <EffectComposer multisampling={quality.antialias ? 4 : 0}>
      <DepthOfField
        focusDistance={0.014}
        focalLength={0.03}
        bokehScale={0}
        height={480}
      />
      <Bloom
        intensity={quality.bloom ? quality.bloomIntensity : 0}
        luminanceThreshold={0.75}
        luminanceSmoothing={0.45}
        mipmapBlur
      />
      <ChromaticAberration
        blendFunction={BlendFunction.NORMAL}
        offset={caOffset.current}
      />
      <Vignette eskil={false} offset={0.2} darkness={0.55} />
      <ToneMapping mode={ToneMappingMode.ACES_FILMIC} />
    </EffectComposer>
  );
}

export function GameCanvas() {
  const phase = useGameStore((s) => s.phase);
  const tunnelWarming = useGameStore((s) => s.tunnelWarming);
  const quality = useGameStore((s) => s.quality);
  const exitBeat = useTunnelExitStore((s) => s.beat);
  const inTunnel = phase === 'tunnel' || tunnelWarming;
  const showWorld = inTunnel || phase === 'run';
  const exitCinematic =
    inTunnel && (exitBeat === 'exit' || exitBeat === 'fade');
  const voBlack = exitBeat === 'fade';

  if (!showWorld) return null;

  return (
    <div
      className="canvas-host"
      style={voBlack ? { visibility: 'hidden' } : undefined}
    >
      <Canvas
        dpr={exitCinematic ? 1 : quality.dpr}
        frameloop={voBlack ? 'never' : 'always'}
        style={{ width: '100%', height: '100%', display: 'block' }}
        gl={{
          antialias: exitCinematic ? false : quality.antialias,
          powerPreference: 'high-performance',
          toneMapping: THREE.NoToneMapping,
          failIfMajorPerformanceCaveat: false,
        }}
        shadows={exitCinematic ? false : quality.shadows}
        camera={{
          position: inTunnel ? [0, 1.55, 0.8] : [0, 1.35, 3.4],
          fov: inTunnel ? 50 : 52,
          near: 0.08,
          far: 140,
        }}
        onCreated={({ gl }) => {
          gl.setClearColor('#05070b');
          gl.outputColorSpace = THREE.SRGBColorSpace;
          const canvas = gl.domElement;
          const w = window as unknown as Record<string, unknown>;
          w.__webglLost = false;
          const onLost = (e: Event) => {
            e.preventDefault();
            w.__webglLost = true;
            console.error(
              '%c[webgl]',
              'color:#ff4444;font-weight:bold',
              'CONTEXT LOST — canvas will stop painting (hero can be in scene but invisible)',
              {
                phase: useGameStore.getState().phase,
                exitBeat: useTunnelExitStore.getState().beat,
                runnerZ: useGameStore.getState().runnerZ,
              },
            );
          };
          const onRestored = () => {
            w.__webglLost = false;
            console.warn(
              '%c[webgl]',
              'color:#D3E671;font-weight:bold',
              'CONTEXT RESTORED',
            );
          };
          canvas.addEventListener('webglcontextlost', onLost, false);
          canvas.addEventListener('webglcontextrestored', onRestored, false);
        }}
      >
        <Suspense fallback={null}>
          <AdaptiveDpr pixelated />
          <FullscreenCanvasSync />
          {inTunnel && <TunnelScene />}
          {phase === 'run' && <RunWorld />}

          <PostFX tunnel={inTunnel} />
        </Suspense>
      </Canvas>
    </div>
  );
}
